// Expenses data layer (Phase 4, P4-CC3) — the live purchases / input-VAT source.
//
// The SINGLE interface between the app and public.expenses
// (supabase/migrations/20260706120000_expenses.sql).
//
// SECURITY MODEL (server-enforced; mirrored here):
//   • We NEVER send company_id or expense_number — DB triggers own them (company
//     forced; expense_number auto = EXP-00001; both immutable). RLS scopes all.
//
// VAT: entry is VAT-INCLUSIVE (a receipt total + rate). computeVat derives subtotal
// and vat_amount, which we store; vat_rate is stored per expense so a per-band RTD
// breakdown is possible later. The VAT Return's T2 uses the TOTAL reclaimable VAT
// (getVatPurchasesBreakdown), mirroring CC2's getVatSalesBreakdown on the sales side.
//
// RETURN-TYPE CONTRACT (consistent with the other modules):
//   collection → { data, count, page?, pageSize? } · single → T|null ·
//   create/update → the affected T · delete → void · all THROW a mapped Error.

import { supabase } from "./supabaseClient";

const T = "expenses";

export const DEFAULT_PAGE_SIZE = 25;

/** Categories (value + label) — parity with the DB CHECK / Base44 set. */
export const EXPENSE_CATEGORIES = [
  { value: "fuel", label: "Fuel" },
  { value: "stationary", label: "Stationery" },
  { value: "utilities", label: "Utilities" },
  { value: "travel", label: "Travel" },
  { value: "meals", label: "Meals" },
  { value: "equipment", label: "Equipment" },
  { value: "software", label: "Software" },
  { value: "marketing", label: "Marketing" },
  { value: "professional_services", label: "Professional services" },
  { value: "rent", label: "Rent" },
  { value: "insurance", label: "Insurance" },
  { value: "repairs", label: "Repairs" },
  { value: "stock", label: "Stock" },
  { value: "other", label: "Other" },
];
export const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "other", label: "Other" },
];
export const VAT_RATES = [0, 9, 13.5, 23];

export const EXPENSE_SORTS = [
  { value: "date", label: "Date" },
  { value: "supplier", label: "Supplier" },
  { value: "category", label: "Category" },
  { value: "total", label: "Total" },
  { value: "created", label: "Newest" },
];

const WRITE_FIELDS = ["expense_date", "supplier", "description", "category", "payment_method", "vat_rate", "vat_reclaimable", "total_amount", "notes", "receipt_url"];
const SORT_COLUMNS = { date: "expense_date", supplier: "supplier", category: "category", total: "total_amount", created: "created_at", number: "expense_number" };
const SELECT =
  "id, expense_number, expense_date, supplier, description, category, payment_method, " +
  "subtotal, vat_rate, vat_amount, vat_reclaimable, total_amount, notes, receipt_url, created_at, updated_at";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = "Invalid value — check the category or payment method."; break;
    case "23505": message = "An expense with this number already exists."; break;
    case "23502": message = "A required field is missing."; break;
    case "42501": message = "You must belong to a company to record expenses."; break;
    case "PGRST116": message = "Expense not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

/**
 * Derive net + VAT from a VAT-INCLUSIVE total and a rate.
 * @param {number} total gross (incl. VAT) @param {number} rate percent
 * @returns {{subtotal:number, vat_amount:number}}
 */
export function computeVat(total, rate) {
  const t = Number(total) || 0;
  const r = Number(rate) || 0;
  const vat_amount = round2(t - t / (1 + r / 100));
  return { subtotal: round2(t - vat_amount), vat_amount };
}

function rowFrom(payload) {
  return { ...pick(payload || {}, WRITE_FIELDS), ...computeVat(payload?.total_amount, payload?.vat_rate) };
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List expenses (RLS-scoped) with search, category + date filters, sort, pagination.
 * @param {Object} [opts]
 * @returns {Promise<{data:Array,count:number,page:number,pageSize:number}>}
 */
export async function getExpenses(opts = {}) {
  const { search, category, from: dFrom, to: dTo, page = 1, pageSize = DEFAULT_PAGE_SIZE, sortBy = "date", sortDir = "desc" } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "expense_date";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const start = (safePage - 1) * safeSize;
  let q = c.from(T).select(SELECT, { count: "exact" }).order(column, { ascending: sortDir !== "desc" }).range(start, start + safeSize - 1);
  if (category && category !== "all") q = q.eq("category", category);
  if (dFrom) q = q.gte("expense_date", dFrom);
  if (dTo) q = q.lte("expense_date", dTo);
  const term = sanitizeTerm(search);
  if (term) q = q.or(`supplier.ilike.%${term}%,description.ilike.%${term}%,expense_number.ilike.%${term}%,category.ilike.%${term}%`);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load expenses");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

export async function searchExpenses(term, opts = {}) { return getExpenses({ ...opts, search: term }); }

/** A single expense, or null. */
export async function getExpense(id) {
  const { data, error } = await client().from(T).select(SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load expense");
  return data || null;
}

/**
 * P4-CC19b — totals across ALL expenses, aggregated SERVER-SIDE
 * (public.expense_stats, 20260811120000_cc19b_aggregates.sql). Previously a
 * client-side sum over a fetch PostgREST silently capped at 1,000 rows.
 *
 * `count` was dropped in CC19b — nothing consumed it.
 * @returns {Promise<{totalExVat:number, totalIncVat:number, vatReclaimable:number}>}
 */
export async function getExpenseStats() {
  const { data, error } = await client().rpc("expense_stats");
  if (error) throw toError(error, "Could not load expense stats");
  const r = (data || [])[0] || {};
  return {
    totalExVat: Number(r.total_ex_vat) || 0,
    totalIncVat: Number(r.total_inc_vat) || 0,
    vatReclaimable: Number(r.vat_reclaimable) || 0,
  };
}

/**
 * TEMPORARY (P4-CC19b Phase 1) — pre-CC19b implementation, kept ONLY for
 * supabase/verify/cc19b_aggregates.verify.mjs to diff against the SQL version.
 * Carries the silent 1,000-row truncation; nothing in src/ may call it.
 * DELETE once the parity harness has been run and accepted.
 */
export async function getExpenseStatsLegacy() {
  const { data, error } = await client().from(T).select("subtotal, total_amount, vat_amount, vat_reclaimable").range(0, 9999);
  if (error) throw toError(error, "Could not load expense stats");
  let totalExVat = 0, totalIncVat = 0, vatReclaimable = 0;
  for (const r of data || []) {
    totalExVat += Number(r.subtotal) || 0;
    totalIncVat += Number(r.total_amount) || 0;
    if (r.vat_reclaimable) vatReclaimable += Number(r.vat_amount) || 0;
  }
  return { totalExVat: round2(totalExVat), totalIncVat: round2(totalIncVat), vatReclaimable: round2(vatReclaimable), count: (data || []).length };
}

/**
 * VAT on purchases (input / T2) over [start,end] by expense_date. purchases_total_ex_vat
 * is ALL expenses' net; total_vat_reclaimable is reclaimable VAT only (what T2 uses).
 * Mirrors CC2's getVatSalesBreakdown. Zero-safe for an empty company.
 * @param {string} start ISO date @param {string} end ISO date
 * @returns {Promise<{purchases_total_ex_vat:number, total_vat_reclaimable:number}>}
 */
export async function getVatPurchasesBreakdown(start, end) {
  // P4-CC19a-2 — count-based truncation detection; see invoices.js's
  // getVatSalesBreakdown for the full rationale. The old "range(0,10000), throw
  // if the 10001st row comes back" guard was structurally unfireable: PostgREST's
  // server-side max_rows (confirmed live: Project Settings -> Data API -> Max
  // Rows = 1000) caps EVERY response regardless of the range a client requests.
  let q = client().from(T).select("subtotal, vat_amount, vat_reclaimable, expense_date", { count: "exact" });
  if (start) q = q.gte("expense_date", start);
  if (end) q = q.lte("expense_date", end);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load VAT on purchases");
  // count == null is untrustworthy, not "0 rows" — defaulting a missing count to
  // 0 would silently disable this guard on a statutory filing figure.
  if (count == null || count > (data || []).length) {
    const e = new Error("Too many records in this period to total automatically. Contact support before filing.");
    e.code = "TOO_MANY_ROWS";
    throw e;
  }
  let purchases_total_ex_vat = 0, total_vat_reclaimable = 0;
  for (const r of data || []) {
    purchases_total_ex_vat += Number(r.subtotal) || 0;
    if (r.vat_reclaimable) total_vat_reclaimable += Number(r.vat_amount) || 0;
  }
  return { purchases_total_ex_vat: round2(purchases_total_ex_vat), total_vat_reclaimable: round2(total_vat_reclaimable) };
}

// ── Writes ───────────────────────────────────────────────────────────────────

/** Create an expense (subtotal + vat_amount derived from total + rate). */
export async function createExpense(payload) {
  const { data, error } = await client().from(T).insert(rowFrom(payload)).select(SELECT).single();
  if (error) throw toError(error, "Could not create expense");
  return data;
}

/** Update an expense (re-derives subtotal + vat_amount). */
export async function updateExpense(id, payload) {
  const { data, error } = await client().from(T).update(rowFrom(payload)).eq("id", id).select(SELECT).single();
  if (error) throw toError(error, "Could not update expense");
  return data;
}

/** Delete an expense. */
export async function deleteExpense(id) {
  const { error } = await client().from(T).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete expense");
}

// ── Receipts (CC4) — private, company-scoped Storage; mirrors productMedia's ─────
// upload/sign/remove trio. Objects live at <company_id>/<expense_id>/<uuid>.<ext>;
// expenses.receipt_url holds the object PATH, signed on read. Capture/store/display
// only — NO OCR (a separate future WP).
const RECEIPTS_BUCKET = "receipts";

// uuid that works in the browser AND the Node verify harness (no window in Node).
const rid = () => ((typeof window !== "undefined" && window.crypto?.randomUUID)
  ? window.crypto.randomUUID()
  : `${Date.now()}-${Math.round(Math.random() * 1e9).toString(16)}`);
function assertReceiptFile(file) {
  if (!file || !(file.type?.startsWith("image/") || file.type === "application/pdf")) {
    const e = new Error("Attach an image or PDF receipt."); e.code = "BAD_FILE"; throw e;
  }
}
const extFor = (file) => { const n = file?.name?.includes(".") ? file.name.split(".").pop().toLowerCase() : ""; return n || (file?.type === "application/pdf" ? "pdf" : "jpg"); };
async function currentCompanyId() {
  const { data, error } = await client().rpc("current_company_id");
  if (error) throw toError(error, "Could not determine your company");
  if (!data) throw new Error("You must belong to a company to attach receipts.");
  return data;
}
/** True if a stored receipt path is a PDF (UI shows a link instead of an <img>). */
export const receiptIsPdf = (path) => /\.pdf$/i.test(String(path || ""));

/**
 * Upload a receipt (image/PDF) to the company-prefixed path and link it to the
 * expense (receipt_url = object path). Replaces + cleans up any prior receipt.
 * @param {string} expenseId @param {File|Blob} file
 * @returns {Promise<Object>} the updated expense.
 */
export async function uploadReceipt(expenseId, file) {
  const c = client();
  assertReceiptFile(file);
  const companyId = await currentCompanyId();
  const path = `${companyId}/${expenseId}/${rid()}.${extFor(file)}`;
  // cacheControl: '0' — receipts are the highest-sensitivity bucket this app holds
  // (routinely contain personal data) and must never sit in the Cloudflare edge
  // cache. Supabase Storage's platform default (Cache-Control: max-age=3600) keeps a
  // PREVIOUSLY-fetched object retrievable at its old URL for up to an hour after
  // real, verified backend deletion — confirmed directly (CC22 Phase 2: an unwarmed
  // path 404s immediately after erasure; a warmed one keeps serving stale bytes,
  // no-cache request headers or not). This is the only lever this app has over that.
  // Contrast productMedia.js's uploadMedia(), which deliberately does NOT set this —
  // see the comment there for why. Do not "tidy" these two into consistency.
  const { error: upErr } = await c.storage.from(RECEIPTS_BUCKET).upload(path, file, { contentType: file.type, upsert: false, cacheControl: "0" });
  if (upErr) throw toError(upErr, "Could not upload the receipt");
  const prev = (await c.from(T).select("receipt_url").eq("id", expenseId).maybeSingle()).data?.receipt_url;
  const { data, error } = await c.from(T).update({ receipt_url: path }).eq("id", expenseId).select(SELECT).single();
  if (error) { await c.storage.from(RECEIPTS_BUCKET).remove([path]); throw toError(error, "Could not link the receipt"); } // roll back orphan
  if (prev && prev !== path) await c.storage.from(RECEIPTS_BUCKET).remove([prev]); // best-effort old-object cleanup
  return data;
}

/**
 * A fresh time-limited signed URL for a receipt (accepts an expense row or a path).
 * @returns {Promise<string|null>}
 */
export async function getReceiptUrl(expenseOrPath, ttl = 3600) {
  const path = typeof expenseOrPath === "string" ? expenseOrPath : expenseOrPath?.receipt_url;
  if (!path) return null;
  const { data, error } = await client().storage.from(RECEIPTS_BUCKET).createSignedUrl(path, ttl);
  if (error) throw toError(error, "Could not open the receipt");
  return data?.signedUrl || null;
}

/** Remove an expense's receipt: delete the object + null receipt_url. */
export async function clearReceipt(expenseId) {
  const c = client();
  const prev = (await c.from(T).select("receipt_url").eq("id", expenseId).maybeSingle()).data?.receipt_url;
  const { data, error } = await c.from(T).update({ receipt_url: null }).eq("id", expenseId).select(SELECT).single();
  if (error) throw toError(error, "Could not remove the receipt");
  if (prev) await c.storage.from(RECEIPTS_BUCKET).remove([prev]); // best-effort object cleanup
  return data;
}
